import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { getUserOverview } from 'src/api/users';
import { LoadingLoop, OrganizationNotFoundScreen } from 'src/components';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { UserOverview } from 'src/types/users';
import { classnames } from 'src/utils/classnames';
import { StudyCard } from 'src/components/studies/study-card';

import { OrganizationCard } from 'src/components/organizations/organization-card';
import { useTranslation } from 'react-i18next';
import { DataSourceCard } from 'src/components/data-sources/data-source-card';

export const OverviewPage = () => {
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);
  const { t } = useTranslation();

  const { isLoading, isError, data } = useQuery<UserOverview>({
    queryKey: [account.userId, 'overview'],
    queryFn: async () => {
      return getUserOverview({ token, userId: account.userId });
    },
  });

  if (isError && !data) {
    return (
      <div className="flex flex-col flex-grow bg-red-300 text-red-500 text-lg">
        {t('error_loading_overview')}
      </div>
    );
  }

  if (data && data.organizations.length === 0 && data.organizationalUnits.length === 0) {
    return <OrganizationNotFoundScreen />;
  }

  return (
    <div className="flex flex-col flex-grow h-0 overflow-scroll bg-gray-950 px-10 py-8 gap-4">
      <div className="flex justify-between items-cente pb-4">
        <h1 className="text-2xl text-white font-bold">
          {t('welcome_user', { name: account.givenName + ' ' + account.familyName })}
        </h1>
      </div>

      <Outlet />

      {isLoading && (
        <div
          className={classnames(
            'flex flex-grow items-center justify-center bg-gray-950 relative',
            location.pathname === '/overview' ? '' : '-z-10',
          )}
        >
          <LoadingLoop className="h-16" />
        </div>
      )}

      {data && data.organizations.length > 0 && (
        <>
          <h2 className="flex text-xl text-white font-bold pt-2"> {t('organization_header')}</h2>
          <div className="flex gap-4 flex-wrap">
            {data.organizations.map((organization) => (
              <OrganizationCard key={organization.organizationId} organization={organization} />
            ))}
          </div>
        </>
      )}

      {data && data.organizationalUnits.length > 0 && (
        <>
          <h2 className="flex text-xl text-white font-bold pt-2">
            {t('organizational_units_header')}
          </h2>
          <div className="flex gap-4 flex-wrap">
            {data.organizationalUnits.map((organization) => (
              <OrganizationCard key={organization.organizationId} organization={organization} />
            ))}
          </div>
        </>
      )}

      {data && data.studies.length > 0 && (
        <>
          <h2 className="flex text-xl text-white font-bold pt-2"> {t('studies_header')}</h2>
          <div className="flex gap-4 flex-wrap">
            {data.studies.map((study) => (
              <StudyCard key={study.studyId} study={study} />
            ))}
          </div>
        </>
      )}

      {data && data.dataSources.length > 0 && (
        <>
          <h2 className="text-xl text-white font-bold pt-2"> {t('data_sources_header')}</h2>
          <div className="flex gap-4 flex-wrap">
            {data.dataSources.map((datasource) => (
              <DataSourceCard key={datasource.dataSourceId} dataSource={datasource} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
